/** @jsx jsx */
import { jsx } from '@emotion/react'
import { css } from '@emotion/react/macro'
import DeleteIcon from '@mui/icons-material/Delete'

import ModalHeader from '../ModalHeader'
import ModalBody from '../ModalBody'
import ModalFooter from '../ModalFooter'
import Modal from '../Modal'
import { PrimaryButton, SecondaryButton } from '../../buttons'
import useModal from '../../../../lib/hooks/useModal'
import { errorTextColor } from '../../../../lib/styles/colors'
import { ModalSize } from '../Modal/Modal'

export type ConfirmModalProps = {
  /** Text of Cancel Button */
  cancelText?: string
  /** Body content */
  children: React.ReactNode
  /** Flag for whether the confirm button should be disabled */
  confirmButtonDisabled?: boolean
  /** Text of Confirm Button */
  confirmText?: string
  /** Indicates the modal should be in DANGER mode with danger border and button */
  isDanger?: boolean
  /** Determines visibility of modal */
  isOpen: boolean
  /** Event callback when Cancel Button is triggered */
  onClose: () => void
  /** Event callback when Confirm Button is triggered */
  onConfirm: () => void
  /** Size of modal */
  size?: ModalSize
  /** Title text of modal */
  title: string
}

const defaultProps: Pick<
  ConfirmModalProps,
  'cancelText' | 'confirmButtonDisabled' | 'confirmText' | 'isDanger' | 'size'
> = {
  cancelText: 'Cancel',
  confirmButtonDisabled: false,
  confirmText: 'Confirm',
  isDanger: false,
  size: ModalSize.SM
}

const bodyCss = css`
  padding-bottom: 40px;
  margin-top: 20px;
`
const confirmCss = css`
  margin-left: 12px;
`
const dangerTitleCss = css`
  display: inline-flex;
  align-items: center;

  svg {
    padding-right: 8px;
    color: ${errorTextColor};
  }
`

export const ConfirmModal = ({
  cancelText,
  children,
  confirmButtonDisabled,
  confirmText,
  isDanger,
  isOpen,
  onClose,
  onConfirm,
  size,
  title
}: ConfirmModalProps & typeof defaultProps) => {
  const confirmButton = isDanger ? (
    <PrimaryButton
      css={confirmCss}
      color={errorTextColor}
      disabled={confirmButtonDisabled}
      onClick={onConfirm}
    >
      {confirmText}
    </PrimaryButton>
  ) : (
    <SecondaryButton css={confirmCss} disabled={confirmButtonDisabled} onClick={onConfirm}>
      {confirmText}
    </SecondaryButton>
  )
  const headerTitle = isDanger ? (
    <span css={dangerTitleCss}>
      <DeleteIcon /> {title}
    </span>
  ) : (
    title
  )

  return (
    <Modal isDanger={isDanger} isOpen={isOpen} size={size}>
      <ModalHeader onClose={isDanger ? onClose : undefined} title={headerTitle} />
      <ModalBody css={bodyCss}>{children}</ModalBody>
      <ModalFooter>
        <SecondaryButton onClick={onClose}>{cancelText}</SecondaryButton>
        {confirmButton}
      </ModalFooter>
    </Modal>
  )
}

ConfirmModal.defaultProps = defaultProps

type ConfirmModalApolloProps = {
  cancelText?: string
  children: React.ReactNode
  confirmButtonDisabled?: boolean
  confirmText?: string
  isDanger?: boolean
  isOpen: boolean
  onConfirm: () => void
  size?: ModalSize
  title: string
}

const ConfirmModalWrapper = ({
  cancelText,
  children,
  confirmButtonDisabled,
  confirmText,
  isDanger,
  isOpen,
  onConfirm,
  size,
  title
}: ConfirmModalApolloProps & typeof defaultProps) => {
  const { hideModal } = useModal()
  const onClose = () => hideModal()

  return (
    <ConfirmModal
      cancelText={cancelText}
      confirmButtonDisabled={confirmButtonDisabled}
      confirmText={confirmText}
      isDanger={isDanger}
      isOpen={isOpen}
      onClose={onClose}
      onConfirm={onConfirm}
      size={size}
      title={title}
    >
      {children}
    </ConfirmModal>
  )
}

ConfirmModalWrapper.defaultProps = defaultProps

export default ConfirmModalWrapper
